import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ContactForm } from '../components/ContactForm';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';

const ContactPage = () => {
  return (
    <div data-testid="contact-page">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Get In Touch</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold mt-2 tracking-tight">
              Contact Us
            </h1>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto text-lg">
              Have a question or need assistance? Our friendly team is here to help you 24 hours a day, 7 days a week.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                We'd Love to Hear From You
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Whether you need to book a taxi, have questions about our services, or want to discuss corporate partnerships, 
                we're here to assist. Reach out through any of the channels below or fill out the contact form.
              </p>

              <div className="space-y-6">
                <a 
                  href="tel:1300096685" 
                  className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center group-hover:bg-primary transition-colors duration-200">
                    <Phone className="w-6 h-6 text-primary group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-secondary">Call Us Anytime</h3>
                    <p className="text-primary font-semibold text-lg">1300 096 685</p>
                    <p className="text-sm text-muted-foreground">Available 24/7 for bookings</p>
                  </div>
                </a>

                <a 
                  href="mailto:13cabsbook.com.au@gmail.com" 
                  className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center group-hover:bg-primary transition-colors duration-200">
                    <Mail className="w-6 h-6 text-primary group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-secondary">Email Us</h3>
                    <p className="text-primary font-semibold">13cabsbook.com.au@gmail.com</p>
                    <p className="text-sm text-muted-foreground">We respond within 24 hours</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-secondary">Service Area</h3>
                    <p className="text-muted-foreground">Melbourne & Victoria Wide</p>
                    <p className="text-sm text-muted-foreground">Including all suburbs & regional areas</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-secondary">Operating Hours</h3>
                    <p className="text-muted-foreground">24 Hours, 7 Days a Week</p>
                    <p className="text-sm text-muted-foreground">Including public holidays</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  <span className="text-xs font-bold uppercase tracking-widest text-primary">Send a Message</span>
                </div>
                <h3 className="font-heading text-2xl font-bold text-secondary">
                  Get in Touch
                </h3>
              </div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">FAQ</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2">
              Common Questions
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: "How do I book a taxi?",
                a: "You can book online through our website, call us at 1300 096 685, or email us your booking details. We recommend booking in advance for airport transfers."
              },
              {
                q: "What areas do you service?",
                a: "We provide taxi services across Melbourne and throughout Victoria, including all suburbs, Melbourne Airport, Avalon Airport, and regional areas like Geelong, Ballarat, and Bendigo."
              },
              {
                q: "Can I book for someone else?",
                a: "Absolutely! You can book on behalf of family members, friends, or colleagues. Just provide their contact details and pickup information when booking."
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept cash, EFTPOS, all major credit cards, Cabcharge, and digital wallets. Payment is made directly to the driver at the end of your journey."
              }
            ].map((faq, index) => (
              <div key={index} className="p-6 bg-gray-50 rounded-xl">
                <h3 className="font-heading text-lg font-bold text-secondary mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
